import { CommonModule } from '@angular/common';
import {
    Component,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Subscription, switchMap, timer } from 'rxjs';

import {
    DistrictProcessingInProgress,
    DistrictProcessingInProgressItem
} from '@/interfaces';
import { DistrictProcessingStatisticsService } from '@/service';

/** App/DB timestamps are stored in UTC (Laravel APP_TIMEZONE=UTC). */
const DISPLAY_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

@Component({
    selector: 'app-statistics-in-progress',
    standalone: true,
    imports: [CommonModule, Tag, Tooltip],
    templateUrl: './in-progress.component.html'
})
export class StatisticsInProgressComponent implements OnInit, OnDestroy {
    data: DistrictProcessingInProgress | null = null;
    loading = true;
    error = false;
    lastUpdatedAt: Date | null = null;

    private pollSub: Subscription | null = null;
    private readonly pollMs = 15_000;

    constructor(private service: DistrictProcessingStatisticsService) {}

    ngOnInit(): void {
        this.pollSub = timer(0, this.pollMs)
            .pipe(switchMap(() => this.service.inProgress()))
            .subscribe({
                next: (data) => {
                    this.data = data;
                    this.loading = false;
                    this.error = false;
                    this.lastUpdatedAt = new Date();
                },
                error: () => {
                    this.loading = false;
                    this.error = true;
                }
            });
    }

    ngOnDestroy(): void {
        this.pollSub?.unsubscribe();
    }

    get items(): DistrictProcessingInProgressItem[] {
        if (!this.data) {
            return [];
        }

        return [...(this.data.crawling ?? []), ...(this.data.analyzing ?? [])];
    }

    get totalActive(): number {
        return this.items.length;
    }

    phaseLabel(item: DistrictProcessingInProgressItem): string {
        return item.phase === 'crawl' ? 'Crawl' : 'Analyze';
    }

    phaseSeverity(
        item: DistrictProcessingInProgressItem
    ): 'info' | 'warn' | 'success' | 'secondary' | 'contrast' | 'danger' {
        return item.phase === 'crawl' ? 'success' : 'info';
    }

    progressHint(item: DistrictProcessingInProgressItem): string {
        if (item.phase !== 'analyze') {
            return '';
        }

        const total = item.total_md_files ?? 0;
        const done = item.processed_md_files ?? 0;
        if (total <= 0) {
            return '';
        }

        return `${done.toLocaleString()} / ${total.toLocaleString()} files`;
    }

    /**
     * Parse DB/API datetimes that are UTC but often arrive without a Z suffix
     * (e.g. "2026-08-13 10:15:00"), so the browser won't treat them as local wall time.
     */
    private parseUtcTimestamp(raw: string): Date | null {
        const value = raw.trim();
        if (!value) {
            return null;
        }

        if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)) {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const parsed = new Date(`${normalized}Z`);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    private formatLocal(dt: Date): string {
        return dt.toLocaleString(undefined, {
            timeZone: DISPLAY_TIME_ZONE,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }

    updatedLabel(): string {
        if (!this.lastUpdatedAt) {
            return '';
        }

        return this.formatLocal(this.lastUpdatedAt);
    }

    startedLabel(item: DistrictProcessingInProgressItem): string {
        const raw = item.started_at || item.updated_at;
        if (!raw) {
            return '';
        }

        const started = this.parseUtcTimestamp(raw);
        if (!started) {
            return '';
        }

        const mins = Math.max(0, Math.floor((Date.now() - started.getTime()) / 60_000));
        const absolute = this.formatLocal(started);

        if (mins < 1) {
            return `${absolute} (just now)`;
        }
        if (mins < 60) {
            return `${absolute} (${mins}m ago)`;
        }

        const hours = Math.floor(mins / 60);
        if (hours < 48) {
            return `${absolute} (${hours}h ago)`;
        }

        return absolute;
    }

    startedTooltip(item: DistrictProcessingInProgressItem): string {
        const raw = item.started_at || item.updated_at;
        if (!raw) {
            return '';
        }

        const started = this.parseUtcTimestamp(raw);
        if (!started) {
            return `DB: ${raw} (UTC)`;
        }

        return `${this.formatLocal(started)} · DB UTC: ${raw}`;
    }
}
